import { useCallback, useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import styles from "../styles/CountdownClock.module.css";

interface Props {
  dateTime: number;
  isCompact: boolean;
}

interface Time {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

const PLACEHOLDER = "-";

const initialTime: Time = {
  days: PLACEHOLDER,
  hours: PLACEHOLDER,
  minutes: PLACEHOLDER,
  seconds: PLACEHOLDER,
};

const normaliseInt = (n: number): number => Math.max(Math.floor(n), 0);

const normaliseStr = (n: number): string => String(n).padStart(2, "0");

const CountdownClock = ({ dateTime, isCompact }: Props) => {
  const [time, setTime] = useState<Time>(initialTime);

  const updateTimer = useCallback(() => {
    const diff = dateTime - Date.now();
    const daysInt = normaliseInt(diff / (1000 * 60 * 60 * 24));
    const days = normaliseStr(daysInt);
    let hoursInt =
      normaliseInt(diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60);

    if (isCompact) {
      hoursInt += daysInt * 24;
    }

    const hours = normaliseStr(hoursInt);
    const minutes = normaliseStr(
      normaliseInt(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)))
    );
    const seconds = normaliseStr(
      normaliseInt(Math.floor((diff % (1000 * 60)) / 1000))
    );

    setTime({ days, hours, minutes, seconds });
  }, [setTime, isCompact, dateTime]);

  useEffect(() => {
    const diff = dateTime - Date.now();

    if (diff < 0) {
      setTime({ days: "0", hours: "0", minutes: "0", seconds: "0" });
      return;
    }

    const timer = setInterval(() => {
      updateTimer();
    }, 1000); // clearing interval
    return () => clearInterval(timer);
  }, [updateTimer, dateTime, setTime]);

  return (
    <Grid container className={styles.clock} mb={15}>
      {!isCompact && (
        <>
          <FigureUnit isCompact={isCompact} figure={time.days} text="days" />
          <Colon isCompact={isCompact} />
        </>
      )}
      <FigureUnit isCompact={isCompact} figure={time.hours} text="hrs" />
      <Colon isCompact={isCompact} />
      <FigureUnit isCompact={isCompact} figure={time.minutes} text="mins" />
      <Colon />
      <FigureUnit isCompact={isCompact} figure={time.seconds} text="secs" />
    </Grid>
  );
};

export default CountdownClock;

const Colon = ({ isCompact }: any) => {
  return (
    <Box className={isCompact ? styles.colonCompact : styles.colon}>:</Box>
  );
};

const FigureUnit = ({ figure, text, isCompact }: any) => {
  let tens, units;

  if (figure === PLACEHOLDER) {
    tens = PLACEHOLDER;
    units = PLACEHOLDER;
  } else {
    const figureInt = Number.parseInt(figure);
    tens = Math.floor(figureInt / 10);
    units = figureInt % 10;
  }

  return (
    <Grid
      container
      item
      className={isCompact ? styles.unitCompact : styles.unit}
    >
      <Grid
        item
        className={isCompact ? styles.unitFiguresCompact : styles.unitFigures}
      >
        <Box className={isCompact ? styles.figureCompact : styles.figure}>
          {tens}
        </Box>
        <Box className={isCompact ? styles.figureCompact : styles.figure}>
          {units}
        </Box>
      </Grid>
      <Grid item mt={2}>
        <Box
          className={isCompact ? styles.figureTextCompact : styles.figureText}
        >
          {text}
        </Box>
      </Grid>
    </Grid>
  );
};
